import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PurchaseDocDetail } from "types/InvoiceInbox";
import { setPurchaseDocMarkedPaid, setPurchaseDocPaymentSource } from "services/invoice-inbox.service";
import UserFilterSelect from "components/user-filter-select/UserFilterSelect";
import { isMarkedPaid, isMarkedPaidAutomatically } from "utils/invoiceInboxStatus";
import { formatPrettyDate } from "utils/helperFunctions";
import { Card, RunChange, ToggleSwitch, buttonClass } from "./invoice-doc-ui";

type Props = {
  companyId: string;
  detail: PurchaseDocDetail;
  busy: boolean;
  run: RunChange;
};

/**
 * Whether the document is settled, and who paid it. Two independent facts
 * with their own endpoints: "Paid" says it is settled, "how" says whether the
 * company or an employee (whom the company then owes) paid. Ordinary register
 * work anyone with access to the module may set — not a warning to act on.
 */
export default function InvoiceDocPaymentCard({ companyId, detail, busy, run }: Props) {
  const { t } = useTranslation();
  const { doc } = detail;
  const people = detail.people || {};

  // "Who actually paid this" — a draft, not a straight readout of the server
  // value: 'employee' has to be pickable before an employee is chosen, and
  // the picker's own selection lives here until Save sends it. Resyncs from
  // the server whenever the document's own payment fields change under it —
  // in particular right after this control's own save reloads the document.
  const [sourceDraft, setSourceDraft] = useState<string>("");
  const [employeeDraft, setEmployeeDraft] = useState<string>("");
  useEffect(() => {
    setSourceDraft(doc.paymentSource || "");
    setEmployeeDraft(doc.paidByEmployee || "");
  }, [doc.paymentSource, doc.paidByEmployee]);

  const unchanged = sourceDraft === (doc.paymentSource || "") && employeeDraft === (doc.paidByEmployee || "");
  const employeeMissing = sourceDraft === "employee" && !employeeDraft;
  const sourceLabel = t("invoice_inbox_payment_source_title", { defaultValue: "How this was paid" });

  return (
    <Card title={t("invoice_inbox_payment_card", { defaultValue: "Payment" })}>
      {/* A person ticks `markedPaidAt` here; once the payment import has seen
          the payment (`paidAt`) the tick is locked on — unticking the manual
          mark could not undo it. */}
      <ToggleSwitch
        label={t("invoice_inbox_marked_paid_label", { defaultValue: "Paid" })}
        checked={isMarkedPaid(doc)}
        disabled={busy || Boolean(doc.paidAt)}
        onChange={(checked) => run(() => setPurchaseDocMarkedPaid(companyId, doc.id, checked))}
      />
      {/* The register's tick only says this in a tooltip, which a touch screen never shows. */}
      {doc.paidAt && !doc.markedPaidAt && (
        <p className="text-xs text-gray-500">
          {t("invoice_inbox_paid_toggle_import", { defaultValue: "Paid — from the payment import" })}
        </p>
      )}
      {doc.markedPaidAt && (
        <p className="text-xs text-gray-500">
          {isMarkedPaidAutomatically(doc)
            ? t("invoice_inbox_marked_paid_auto", {
                defaultValue: "Marked automatically: this supplier's invoices arrive paid, {{date}}",
                date: formatPrettyDate(doc.markedPaidAt),
              })
            : t("invoice_inbox_marked_paid_by", {
                defaultValue: "Marked paid by {{name}}, {{date}}",
                name: (doc.markedPaidBy && people[doc.markedPaidBy] && people[doc.markedPaidBy].name) || "—",
                date: formatPrettyDate(doc.markedPaidAt),
              })}
        </p>
      )}

      <div className="mt-2 space-y-2 border-t border-gray-100 pt-2">
        {doc.paymentSource === "employee" && detail.paidByEmployee && (
          <p className="text-xs text-gray-600">
            {t("invoice_inbox_payment_source_current_employee", {
              defaultValue: "Currently marked as paid by {{name}} — the company owes them back.",
              name: detail.paidByEmployee.name,
            })}
          </p>
        )}
        <div className="flex items-center gap-2">
          <select
            aria-label={sourceLabel}
            title={sourceLabel}
            className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-base md:text-sm disabled:opacity-50"
            value={sourceDraft}
            disabled={busy}
            onChange={(event) => {
              const value = event.target.value;
              setSourceDraft(value);
              // Switching away from 'employee' drops a half-picked selection —
              // leaving it would let a later switch back resubmit somebody
              // chosen for a different value.
              if (value !== "employee") setEmployeeDraft("");
            }}
          >
            <option value="">{t("invoice_inbox_payment_source_unset", { defaultValue: "Not stated" })}</option>
            <option value="company">
              {t("invoice_inbox_payment_source_company", { defaultValue: "Paid from the company account" })}
            </option>
            <option value="employee">
              {t("invoice_inbox_payment_source_employee", { defaultValue: "Paid by an employee" })}
            </option>
          </select>
          <button
            type="button"
            className={buttonClass}
            disabled={busy || unchanged || employeeMissing}
            onClick={() =>
              run(() =>
                setPurchaseDocPaymentSource(companyId, doc.id, {
                  source: sourceDraft === "" ? null : (sourceDraft as "company" | "employee"),
                  employeeId: sourceDraft === "employee" ? employeeDraft : undefined,
                })
              )
            }
          >
            {t("save", { defaultValue: "Save" })}
          </button>
        </div>
        {sourceDraft === "employee" && (
          <UserFilterSelect
            value={employeeDraft}
            onChange={setEmployeeDraft}
            label={t("invoice_inbox_payment_source_employee_picker", { defaultValue: "Which employee?" })}
          />
        )}
      </div>
    </Card>
  );
}
